/**
 * PhoneAuthService - Professional Firebase Phone Authentication Service
 *
 * Features:
 * - Reactive state management with BehaviorSubjects
 * - Invisible reCAPTCHA integration
 * - OTP verification with retry logic
 * - Cooldown timer for resend functionality
 * - Comprehensive error handling with i18n support
 * - Rate limiting protection
 * - Session management
 *
 * @see https://firebase.google.com/docs/auth/web/phone-auth
 */

import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { Auth, signInWithEmailAndPassword, signInWithPhoneNumber, linkWithPhoneNumber, RecaptchaVerifier, ConfirmationResult, User } from '@angular/fire/auth';
import { Firestore, collection, query, where, limit, getDocs, doc, getDoc, updateDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { BehaviorSubject, Observable, Subject, interval, takeUntil } from 'rxjs';
import { arrayUnion, serverTimestamp } from '@angular/fire/firestore';
import { environment } from '../../environment';
import { UserProfile, PhoneAuthLinkMode, PhoneAuthLinkState } from '../models/user-profile';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type PhoneAuthStep = 'phone_input' | 'account_check' | 'email_link' | 'password_input' | 'existing_provider_login' | 'otp_verification' | 'success' | 'error';

export interface PhoneAuthState {
  step: PhoneAuthStep;
  isLoading: boolean;
  phoneNumber: string;
  formattedPhoneNumber: string;
  email: string; // Email entered by user (for MODE LINK)
  password: string; // Password for email authentication (for MODE LINK)
  error: PhoneAuthError | null;
  canResend: boolean;
  resendCooldown: number;
  attemptCount: number;
  maxAttempts: number;
  profileComplete: boolean; // Whether user has completed their profile (has fullname)
  linkState: PhoneAuthLinkState | null; // Account linking state
}

export interface PhoneAuthError {
  code: string;
  message: string;
  translationKey: string;
}

export interface PhonePrefix {
  value: string;
  short: string;
  country: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_STATE: PhoneAuthState = {
  step: 'phone_input',
  isLoading: false,
  phoneNumber: '',
  formattedPhoneNumber: '',
  email: '',
  password: '',
  error: null,
  canResend: false,
  resendCooldown: 0,
  attemptCount: 0,
  maxAttempts: 3,
  profileComplete: false,
  linkState: null
};

const RESEND_COOLDOWN_SECONDS = 60;
const OTP_LENGTH = 6;
const MAX_VERIFICATION_ATTEMPTS = 3;

// DPD Supported Countries (EU + Key Markets)
const DPD_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'CH', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI',
  'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT',
  'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'RS', 'SK', 'SI',
  'ES', 'SE', 'GB'
];

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class PhoneAuthService implements OnDestroy {

  // State management
  private stateSubject = new BehaviorSubject<PhoneAuthState>(INITIAL_STATE);
  public state$ = this.stateSubject.asObservable();

  // Firebase objects
  private recaptchaVerifier: RecaptchaVerifier | null = null;
  private confirmationResult: ConfirmationResult | null = null;

  // Timers
  private cooldownTimer$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  // Phone prefixes
  private _phonePrefixes: PhonePrefix[] = [];
  private _dpdPhonePrefixes: PhonePrefix[] = [];

  constructor(
    private auth: Auth,
    private firestore: Firestore,
    private functions: Functions,
    private ngZone: NgZone
  ) {
    this.initializePhonePrefixes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cooldownTimer$.next();
    this.cooldownTimer$.complete();
    this.cleanupRecaptcha();
  }

  // ============================================================================
  // PUBLIC API - GETTERS
  // ============================================================================

  get state(): PhoneAuthState {
    return this.stateSubject.value;
  }

  get phonePrefixes(): PhonePrefix[] {
    return this._phonePrefixes;
  }

  get dpdPhonePrefixes(): PhonePrefix[] {
    return this._dpdPhonePrefixes;
  }

  get defaultPrefix(): PhonePrefix | undefined {
    // Default to Belgium (+32)
    return this._dpdPhonePrefixes.find(p => p.value === '+32') || this._dpdPhonePrefixes[0];
  }

  // ============================================================================
  // PUBLIC API - ACTIONS
  // ============================================================================

  /**
   * Initialize the reCAPTCHA verifier
   * Must be called after the DOM element is available
   */
  async initializeRecaptcha(containerId: string): Promise<void> {
    try {
      console.log('📱 [PhoneAuth] Initializing reCAPTCHA...');

      // Clean up any existing verifier
      this.cleanupRecaptcha();

      // Clear the container
      const container = document.getElementById(containerId);
      if (container) {
        container.innerHTML = '';
      }

      // Create invisible reCAPTCHA
      this.recaptchaVerifier = new RecaptchaVerifier(this.auth, containerId, {
        size: 'invisible',
        callback: () => {
          console.log('✅ [PhoneAuth] reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.log('⚠️ [PhoneAuth] reCAPTCHA expired');
          this.ngZone.run(() => {
            this.updateState({
              error: {
                code: 'recaptcha-expired',
                message: 'reCAPTCHA session expired',
                translationKey: 'AUTH.PHONE.ERRORS.SESSION_EXPIRED'
              }
            });
          });
        }
      });

      await this.recaptchaVerifier.render();
      console.log('✅ [PhoneAuth] reCAPTCHA ready');

    } catch (error) {
      console.error('❌ [PhoneAuth] reCAPTCHA initialization failed:', error);
      throw error;
    }
  }

  /**
   * STEP 0: Check if phone exists in Firestore BEFORE sending OTP
   * Uses secure Cloud Function with rate limiting
   * This prevents duplicate accounts
   */
  async checkPhoneExists(phonePrefix: string, phoneNumber: string): Promise<PhoneAuthLinkState> {
    const cleanNumber = phoneNumber.replace(/\s/g, '').replace(/^0+/, '');
    const formattedPhone = `${phonePrefix}${cleanNumber}`;

    this.updateState({
      isLoading: true,
      error: null,
      phoneNumber: cleanNumber,
      formattedPhoneNumber: formattedPhone
    });

    console.log('🔍 [PhoneAuth] Checking if phone exists (via Cloud Function):', formattedPhone);

    try {
      // Call secure Cloud Function (with rate limiting)
      const checkPhoneFunction = httpsCallable(this.functions, 'checkPhoneExists');
      const response = await checkPhoneFunction({ phoneNumber: formattedPhone });
      const result = response.data as any;

      const exists = result?.exists || false;
      console.log(`🔍 [PhoneAuth] Cloud Function result - Phone exists: ${exists}`);

      if (exists) {
        // Phone found in existing profile - profile data returned from Cloud Function
        const existingProfile = result.profile as UserProfile;

        if (existingProfile) {
          console.log('✅ [PhoneAuth] Phone exists in profile:', existingProfile.uid);
          console.log('📊 [PhoneAuth] Profile authProviders:', existingProfile.authProviders);

          // Check what providers the existing profile has
          const hasPhoneProvider = existingProfile.authProviders?.includes('phone');
          const hasGoogleProvider = existingProfile.authProviders?.includes('google');
          const hasEmailProvider = existingProfile.authProviders?.includes('email');

          // CASE 1: User already has phone provider - can sign in directly with phone
          if (hasPhoneProvider) {
            console.log('📱 [PhoneAuth] User already has phone provider - direct sign in');

            const linkState: PhoneAuthLinkState = {
              mode: 'existing',
              existingProfile,
              phoneNumber: formattedPhone
            };

            this.updateState({
              isLoading: false,
              linkState,
              step: 'otp_verification' // Go directly to OTP
            });

            return linkState;
          }

          // CASE 2: User has Google/Email but NOT phone - need to login first then link
          if (hasGoogleProvider || hasEmailProvider) {
            console.log('🔗 [PhoneAuth] User has Google/Email but no phone - need to login first');

            const linkState: PhoneAuthLinkState = {
              mode: 'existing',
              existingProfile,
              phoneNumber: formattedPhone,
              // Store which provider they need to use
              existingProvider: hasGoogleProvider ? 'google' : 'email'
            };

            this.updateState({
              isLoading: false,
              linkState,
              step: 'existing_provider_login', // New step: ask to login with existing provider
              email: existingProfile.email || undefined
            });

            return linkState;
          }

          // CASE 3: Profile exists but authProviders is undefined/empty
          // Check Firebase Auth directly to find the real provider
          console.log('⚠️ [PhoneAuth] Profile exists but no authProviders in Firestore - checking Firebase Auth...');

          if (existingProfile.email) {
            // Call getUserProviders to check Firebase Auth
            const getUserProvidersFunction = httpsCallable(this.functions, 'getUserProviders');
            const providersResponse = await getUserProvidersFunction({ email: existingProfile.email });
            const providersResult = providersResponse.data as any;

            console.log('📊 [PhoneAuth] Firebase Auth providers result:', providersResult);

            const hasGoogle = providersResult?.hasGoogle || false;
            const hasPassword = providersResult?.hasPassword || false;
            const authExists = providersResult?.exists || false;

            // If user exists in Firebase Auth with Google or Password, redirect to existing_provider_login
            if (authExists && (hasGoogle || hasPassword)) {
              console.log('🔗 [PhoneAuth] User found in Firebase Auth - redirecting to existing_provider_login');

              const linkState: PhoneAuthLinkState = {
                mode: 'existing',
                existingProfile,
                phoneNumber: formattedPhone,
                existingProvider: hasGoogle ? 'google' : 'email'
              };

              this.updateState({
                isLoading: false,
                linkState,
                step: 'existing_provider_login',
                email: existingProfile.email
              });

              return linkState;
            }
          }

          // No email or no Firebase Auth account - allow direct OTP (truly new phone user)
          console.log('📱 [PhoneAuth] No Firebase Auth account found - allowing direct OTP');

          const linkState: PhoneAuthLinkState = {
            mode: 'existing',
            existingProfile,
            phoneNumber: formattedPhone
          };

          this.updateState({
            isLoading: false,
            linkState,
            step: 'otp_verification'
          });

          return linkState;
        }
      }

      // Phone NOT found - ask user if they have an account
      console.log('❌ [PhoneAuth] Phone not found in Firestore');

      const linkState: PhoneAuthLinkState = {
        mode: 'new', // Default to new, will change if user says "yes I have account"
        phoneNumber: formattedPhone
      };

      this.updateState({
        isLoading: false,
        linkState,
        step: 'account_check' // Ask: "Do you have an account?"
      });

      return linkState;

    } catch (error: any) {
      console.error('❌ [PhoneAuth] Error checking phone:', error);

      // Handle rate limiting errors with user-friendly message
      if (error?.code === 'functions/resource-exhausted') {
        this.updateState({
          isLoading: false,
          error: {
            code: 'rate-limit-exceeded',
            message: error.message,
            translationKey: 'AUTH.PHONE.ERRORS.RATE_LIMIT'
          }
        });
      } else {
        this.updateState({ isLoading: false });
      }

      throw error;
    }
  }

  /**
   * Fetch user profile by phone number (authenticated query)
   * Called AFTER Cloud Function confirms phone exists
   */
  private async fetchProfileByPhone(phoneNumber: string): Promise<UserProfile | null> {
    try {
      const usersRef = collection(this.firestore, 'users');
      const q = query(usersRef, where('phoneNumber', '==', phoneNumber), limit(1));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return snapshot.docs[0].data() as UserProfile;
      }

      return null;
    } catch (error) {
      console.error('❌ [PhoneAuth] Failed to fetch profile by phone:', error);
      return null;
    }
  }

  /**
   * User answered "YES, I have an account" - ask for email to link
   */
  setUserHasAccount(): void {
    this.updateState({
      step: 'email_link'
    });
  }

  /**
   * User answered "NO, new account" - proceed to OTP
   */
  setUserNewAccount(): void {
    const currentLinkState = this.state.linkState;
    if (currentLinkState) {
      this.updateState({
        linkState: { ...currentLinkState, mode: 'new' },
        step: 'otp_verification'
      });
    }
  }

  /**
   * Search for existing profile by email (for account linking)
   * Uses secure Cloud Function with rate limiting
   */
  async findProfileByEmail(email: string): Promise<UserProfile | null> {
    this.updateState({ isLoading: true, error: null });

    const emailLowercase = email.toLowerCase().trim();
    console.log('🔍 [PhoneAuth] Searching profile by email (via Cloud Function):', emailLowercase);

    try {
      // Call secure Cloud Function (with rate limiting)
      const checkEmailFunction = httpsCallable(this.functions, 'checkEmailExists');
      const response = await checkEmailFunction({ email: emailLowercase });
      const result = response.data as any;

      const exists = result?.exists || false;
      console.log(`🔍 [PhoneAuth] Cloud Function result - Email exists: ${exists}`);

      if (exists) {
        // Email found - profile data returned from Cloud Function
        const profile = result.profile as UserProfile;

        if (profile) {
          console.log('✅ [PhoneAuth] Profile found by email:', profile.uid);

          // Check what providers this user has using Cloud Function (Firebase Admin SDK)
          // Note: fetchSignInMethodsForEmail is deprecated and blocked by Email Enumeration Protection
          console.log('🔍 [PhoneAuth] Checking user providers via Cloud Function getUserProviders...');

          const getUserProvidersFunction = httpsCallable(this.functions, 'getUserProviders');
          const providersResponse = await getUserProvidersFunction({ email: emailLowercase });
          const providersResult = providersResponse.data as any;

          console.log('📊 [PhoneAuth] getUserProviders result:', providersResult);

          const hasPassword = providersResult?.hasPassword || false;
          const hasGoogle = providersResult?.hasGoogle || false;
          const providers = providersResult?.providers || [];
          const authExists = providersResult?.exists || false;

          console.log(`🔐 [PhoneAuth] User auth methods - Password: ${hasPassword}, Google: ${hasGoogle}, Exists in Auth: ${authExists}, Providers: ${providers.join(', ')}`);

          // Update link state with found profile
          const currentLinkState = this.state.linkState;
          if (currentLinkState) {
            // CASE 0: No auth providers found - profile exists in Firestore but not in Firebase Auth
            if (!authExists || providers.length === 0) {
              console.log('⚠️ [PhoneAuth] No auth providers found - account may have been deleted from Firebase Auth');
              this.updateState({
                isLoading: false,
                email: emailLowercase,
                error: {
                  code: 'account-not-found-in-auth',
                  message: 'No authentication account found for this email. Please create a new account or try a different email.',
                  translationKey: 'AUTH.PHONE.ERRORS.ACCOUNT_NOT_IN_AUTH'
                }
              });
            }
            // CASE 1: User has password auth → Ask for password
            else if (hasPassword) {
              console.log('✅ [PhoneAuth] User has password auth, proceeding to password_input step');
              this.updateState({
                isLoading: false,
                email: emailLowercase,
                linkState: {
                  ...currentLinkState,
                  mode: 'link',
                  existingProfile: profile
                },
                step: 'password_input'
              });
            }
            // CASE 2: User has Google auth (or other provider) but NO password
            else if (hasGoogle) {
              console.log('⚠️ [PhoneAuth] User has Google auth only, cannot use password');
              this.updateState({
                isLoading: false,
                email: emailLowercase,
                linkState: {
                  ...currentLinkState,
                  mode: 'link',
                  existingProfile: profile
                },
                error: {
                  code: 'use-google-auth',
                  message: 'This account uses Google authentication. Please sign in with Google first.',
                  translationKey: 'AUTH.PHONE.ERRORS.USE_GOOGLE_AUTH'
                }
              });
            }
            // CASE 3: Other providers (Facebook, Apple, etc.)
            else {
              console.log('⚠️ [PhoneAuth] User has other auth provider:', providers);
              this.updateState({
                isLoading: false,
                email: emailLowercase,
                error: {
                  code: 'use-original-provider',
                  message: 'Please sign in using your original authentication method.',
                  translationKey: 'AUTH.PHONE.ERRORS.USE_ORIGINAL_PROVIDER'
                }
              });
            }
          }

          return profile;
        }
      }

      console.log('❌ [PhoneAuth] No profile found with this email');
      this.updateState({ isLoading: false });
      return null;

    } catch (error: any) {
      console.error('❌ [PhoneAuth] Error searching email:', error);

      // Handle rate limiting errors with user-friendly message
      if (error?.code === 'functions/resource-exhausted') {
        this.updateState({
          isLoading: false,
          error: {
            code: 'rate-limit-exceeded',
            message: error.message,
            translationKey: 'AUTH.PHONE.ERRORS.RATE_LIMIT'
          }
        });
      } else {
        this.updateState({ isLoading: false });
      }

      return null;
    }
  }

  /**
   * Fetch user profile by email (authenticated query)
   * Called AFTER Cloud Function confirms email exists
   */
  private async fetchProfileByEmail(email: string): Promise<UserProfile | null> {
    try {
      const usersRef = collection(this.firestore, 'users');
      const q = query(usersRef, where('email', '==', email), limit(1));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        return snapshot.docs[0].data() as UserProfile;
      }

      return null;
    } catch (error) {
      console.error('❌ [PhoneAuth] Failed to fetch profile by email:', error);
      return null;
    }
  }

  /**
   * Refresh reCAPTCHA verifier before sending OTP
   * This ensures we have a fresh token for Firebase Auth
   */
  private async refreshRecaptchaVerifier(): Promise<void> {
    try {
      console.log('🔄 [PhoneAuth] Refreshing reCAPTCHA verifier...');

      // Clear existing verifier
      this.cleanupRecaptcha();

      // Get container
      const containerId = 'recaptcha-container';
      const container = document.getElementById(containerId);

      if (!container) {
        throw new Error('reCAPTCHA container not found');
      }

      // CRITICAL: Remove ALL children and recreate a fresh div
      // This prevents "reCAPTCHA has already been rendered" error
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      // Create a new div inside the container for reCAPTCHA
      const recaptchaDiv = document.createElement('div');
      recaptchaDiv.id = 'recaptcha-verifier-div';
      container.appendChild(recaptchaDiv);

      // Create fresh invisible reCAPTCHA on the NEW div
      this.recaptchaVerifier = new RecaptchaVerifier(this.auth, 'recaptcha-verifier-div', {
        size: 'invisible',
        callback: () => {
          console.log('✅ [PhoneAuth] reCAPTCHA verified (refreshed)');
        },
        'expired-callback': () => {
          console.log('⚠️ [PhoneAuth] reCAPTCHA expired (refreshed)');
          this.ngZone.run(() => {
            this.updateState({
              error: {
                code: 'recaptcha-expired',
                message: 'reCAPTCHA session expired',
                translationKey: 'AUTH.PHONE.ERRORS.SESSION_EXPIRED'
              }
            });
          });
        }
      });

      await this.recaptchaVerifier.render();
      console.log('✅ [PhoneAuth] reCAPTCHA verifier refreshed successfully');

    } catch (error) {
      console.error('❌ [PhoneAuth] Failed to refresh reCAPTCHA:', error);
      throw error;
    }
  }

  /**
   * Authenticate with email and password (MODE LINK)
   * Then proceed to link phone number
   */
  async authenticateWithEmailAndPassword(password: string): Promise<boolean> {
    const state = this.state;
    const email = state.email;

    if (!email || !password) {
      this.setError('invalid-credentials', 'AUTH.PHONE.ERRORS.INVALID_CREDENTIALS');
      return false;
    }

    this.updateState({ isLoading: true, error: null, password });

    console.log('🔐 [PhoneAuth] Authenticating with email:', email);

    try {
      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;

      if (!user) {
        throw new Error('No user returned from sign in');
      }

      console.log('✅ [PhoneAuth] Successfully authenticated, UID:', user.uid);

      // Now proceed to OTP step (we'll use linkWithPhoneNumber in sendOTP)
      this.updateState({
        isLoading: false,
        step: 'otp_verification'
      });

      return true;

    } catch (error: any) {
      console.error('❌ [PhoneAuth] Email/password authentication failed:', error);
      this.handleFirebaseError(error);
      return false;
    }
  }

  /**
   * Send OTP to the provided phone number
   */
  async sendOTP(phonePrefix: string, phoneNumber: string): Promise<boolean> {
    const state = this.state;

    // Validate inputs
    const cleanNumber = phoneNumber.replace(/\s/g, '').replace(/^0+/, '');
    if (!cleanNumber || cleanNumber.length < 6) {
      this.setError('invalid-phone', 'AUTH.PHONE.ERRORS.INVALID_PHONE');
      return false;
    }

    // Check rate limiting
    if (state.attemptCount >= state.maxAttempts) {
      this.setError('too-many-attempts', 'AUTH.PHONE.ERRORS.TOO_MANY_ATTEMPTS');
      return false;
    }

    // Format phone number to E.164
    const formattedPhone = `${phonePrefix}${cleanNumber}`;

    this.updateState({
      isLoading: true,
      error: null,
      phoneNumber: cleanNumber,
      formattedPhoneNumber: formattedPhone
    });

    console.log('📤 [PhoneAuth] Sending OTP to:', formattedPhone);

    try {
      if (!this.recaptchaVerifier) {
        throw new Error('reCAPTCHA not initialized');
      }

      // Check if user is already authenticated (MODE LINK)
      const currentUser = this.auth.currentUser;
      const isLinkMode = state.linkState?.mode === 'link';

      if (currentUser && isLinkMode) {
        // MODE LINK: User already signed in, link phone to existing account
        console.log('🔗 [PhoneAuth] MODE LINK detected - Using linkWithPhoneNumber');

        this.confirmationResult = await linkWithPhoneNumber(
          currentUser,
          formattedPhone,
          this.recaptchaVerifier
        );

        console.log('✅ [PhoneAuth] OTP sent for linking (same UID will be preserved)');
      } else {
        // MODE NEW or MODE EXISTING: Standard phone sign in
        console.log('📱 [PhoneAuth] Using signInWithPhoneNumber');

        this.confirmationResult = await signInWithPhoneNumber(
          this.auth,
          formattedPhone,
          this.recaptchaVerifier
        );

        console.log('✅ [PhoneAuth] OTP sent successfully');
      }

      this.updateState({
        isLoading: false,
        step: 'otp_verification',
        attemptCount: state.attemptCount + 1,
        canResend: false,
        resendCooldown: RESEND_COOLDOWN_SECONDS
      });

      // Start cooldown timer
      this.startCooldownTimer();

      return true;

    } catch (error: any) {
      console.error('❌ [PhoneAuth] Send OTP failed:', error);
      this.handleFirebaseError(error);
      return false;
    }
  }

  /**
   * Verify the OTP code entered by the user
   */
  async verifyOTP(code: string): Promise<User | null> {
    // Validate code format
    const cleanCode = code.replace(/\s/g, '');
    if (!cleanCode || cleanCode.length !== OTP_LENGTH) {
      this.setError('invalid-code-format', 'AUTH.PHONE.ERRORS.INVALID_CODE_FORMAT');
      return null;
    }

    if (!this.confirmationResult) {
      this.setError('session-expired', 'AUTH.PHONE.ERRORS.SESSION_EXPIRED');
      this.resetToPhoneInput();
      return null;
    }

    this.updateState({ isLoading: true, error: null });

    console.log('🔐 [PhoneAuth] Verifying OTP...');
    console.log('📊 [PhoneAuth] Current linkState:', JSON.stringify(this.state.linkState));

    try {
      const result = await this.confirmationResult.confirm(cleanCode);
      const user = result.user;

      console.log('✅ [PhoneAuth] Verification successful, user:', user?.uid);

      // Create or update user profile in Firestore and check if complete
      let profileComplete = false;
      if (user) {
        console.log('📝 [PhoneAuth] Calling ensureUserProfile with mode:', this.state.linkState?.mode);
        profileComplete = await this.ensureUserProfile(user);
        console.log('📊 [PhoneAuth] ensureUserProfile returned profileComplete:', profileComplete);
      }

      this.updateState({
        isLoading: false,
        step: 'success',
        profileComplete
      });

      console.log('🎯 [PhoneAuth] Final state - profileComplete:', profileComplete);

      return user || null;

    } catch (error: any) {
      console.error('❌ [PhoneAuth] OTP verification failed:', error);

      // Special handling for "account-exists-with-different-credential"
      // This happens when trying to link a phone number that's already used by another account
      if (error.code === 'auth/account-exists-with-different-credential') {
        console.log('⚠️ [PhoneAuth] Phone number already linked to another account');

        this.updateState({
          isLoading: false,
          error: {
            code: 'phone-already-in-use',
            message: 'This phone number is already linked to another account',
            translationKey: 'AUTH.PHONE.ERRORS.PHONE_ALREADY_IN_USE'
          }
        });
        return null;
      }

      // Special handling for "credential-already-in-use"
      // Similar case - phone credential is already used
      if (error.code === 'auth/credential-already-in-use') {
        console.log('⚠️ [PhoneAuth] Phone credential already in use by another account');

        this.updateState({
          isLoading: false,
          error: {
            code: 'phone-already-in-use',
            message: 'This phone number is already linked to another account',
            translationKey: 'AUTH.PHONE.ERRORS.PHONE_ALREADY_IN_USE'
          }
        });
        return null;
      }

      this.handleFirebaseError(error);
      return null;
    }
  }

  /**
   * Ensure user profile exists in Firestore after phone authentication
   * Handles 3 modes: existing, link, new
   * @returns true if profile is complete (has fullname), false otherwise
   */
  private async ensureUserProfile(user: User): Promise<boolean> {
    const state = this.state;
    const linkState = state.linkState;

    if (!linkState) {
      console.error('❌ [PhoneAuth] No link state found');
      return false;
    }

    try {
      const mode = linkState.mode;
      console.log(`🔗 [PhoneAuth] Linking mode: ${mode}`);

      if (mode === 'existing' && linkState.existingProfile) {
        // MODE EXISTING: Phone found in existing profile
        // Use Cloud Function to link the new Firebase Auth UID to existing profile
        const existingProfile = linkState.existingProfile;

        console.log('🔗 [PhoneAuth] Calling linkPhoneToProfile Cloud Function...');

        const linkPhoneToProfile = httpsCallable(this.functions, 'linkPhoneToProfile');
        const response = await linkPhoneToProfile({
          phoneAuthUid: user.uid,
          existingProfileUid: existingProfile.uid,
          phoneNumber: state.formattedPhoneNumber
        });
        const result = response.data as any;

        console.log('✅ [PhoneAuth] Cloud Function result:', result);

        // Check if profile is complete
        // 🎯 #331-B3 : complet = fullname ET email (aligné sur createPhoneProfile) —
        // sans email, le paiement est bloqué (EMAIL_REQUIRED) → repasser par complete-profile
        const isComplete = !!(
          existingProfile.fullname && existingProfile.fullname.trim() !== '' &&
          existingProfile.email && existingProfile.email.trim() !== ''
        );
        return isComplete;

      } else if (mode === 'link' && linkState.existingProfile) {
        // MODE LINK: Email found, user authenticated, phone linked via linkWithPhoneNumber
        // Firebase Auth already linked the phone provider to the existing account
        // We just need to update the Firestore profile with the phone number
        const existingProfile = linkState.existingProfile;

        console.log('🔗 [PhoneAuth] MODE LINK - Phone already linked by Firebase Auth');
        console.log('📝 [PhoneAuth] Updating Firestore profile with phone number');

        // Update Firestore profile with phone number (user is authenticated, can update own profile)
        try {
          await updateDoc(doc(this.firestore, 'users', user.uid), {
            phoneNumber: state.formattedPhoneNumber,
            authProviders: arrayUnion('phone'),
            updatedAt: serverTimestamp()
          });

          console.log('✅ [PhoneAuth] Profile updated with phone number');
        } catch (error) {
          console.error('❌ [PhoneAuth] Failed to update profile:', error);
          // Non-blocking, continue anyway
        }

        // Check if profile is complete
        // 🎯 #331-B3 : complet = fullname ET email (aligné sur createPhoneProfile) —
        // sans email, le paiement est bloqué (EMAIL_REQUIRED) → repasser par complete-profile
        const isComplete = !!(
          existingProfile.fullname && existingProfile.fullname.trim() !== '' &&
          existingProfile.email && existingProfile.email.trim() !== ''
        );
        return isComplete;

      } else if (mode === 'new') {
        // MODE NEW: Create brand new profile
        // Use Cloud Function to create profile server-side
        console.log('🆕 [PhoneAuth] Calling createPhoneProfile Cloud Function...');
        console.log('🆕 [PhoneAuth] With data:', {
          uid: user.uid,
          phoneNumber: state.formattedPhoneNumber,
          email: user.email || undefined
        });

        const createPhoneProfile = httpsCallable(this.functions, 'createPhoneProfile');
        const response = await createPhoneProfile({
          uid: user.uid,
          phoneNumber: state.formattedPhoneNumber,
          email: user.email || undefined
        });
        const result = response.data as any;

        console.log('✅ [PhoneAuth] Cloud Function result:', JSON.stringify(result));
        console.log('✅ [PhoneAuth] isComplete from result:', result?.isComplete);

        // Return isComplete from Cloud Function response
        const isComplete = result?.isComplete || false;
        console.log('🎯 [PhoneAuth] MODE NEW - returning isComplete:', isComplete);
        return isComplete;
      } else {
        console.warn('⚠️ [PhoneAuth] Unknown mode:', mode);
      }

      console.warn('⚠️ [PhoneAuth] Falling through to return false');
      return false;
    } catch (error) {
      console.error('❌ [PhoneAuth] Failed to ensure user profile:', error);
      return false;
    }
  }

  /**
   * Check if a user profile is complete
   */
  async isProfileComplete(userId: string): Promise<boolean> {
    try {
      const userDoc = await getDoc(doc(this.firestore, 'users', userId));
      if (userDoc.exists()) {
        const profile = userDoc.data() as UserProfile;
        return !!(profile.fullname && profile.fullname.trim() !== '');
      }
      return false;
    } catch (error) {
      console.error('⚠️ [PhoneAuth] Failed to check profile:', error);
      return false;
    }
  }

  /**
   * Resend the OTP code
   */
  async resendOTP(): Promise<boolean> {
    const state = this.state;

    if (!state.canResend) {
      console.log('⏳ [PhoneAuth] Resend cooldown active:', state.resendCooldown, 's');
      return false;
    }

    if (state.attemptCount >= state.maxAttempts) {
      this.setError('too-many-attempts', 'AUTH.PHONE.ERRORS.TOO_MANY_ATTEMPTS');
      return false;
    }

    // Re-initialize reCAPTCHA for new attempt
    await this.initializeRecaptcha('recaptcha-container');

    // Get prefix from formatted number
    const prefix = this.extractPrefix(state.formattedPhoneNumber);

    return this.sendOTP(prefix, state.phoneNumber);
  }

  /**
   * Reset to phone input step
   */
  resetToPhoneInput(): void {
    this.cooldownTimer$.next();
    this.confirmationResult = null;

    this.updateState({
      step: 'phone_input',
      isLoading: false,
      error: null,
      canResend: false,
      resendCooldown: 0
    });
  }

  /**
   * Complete reset of the service state
   */
  reset(): void {
    this.cooldownTimer$.next();
    this.cleanupRecaptcha();
    this.confirmationResult = null;
    this.stateSubject.next({ ...INITIAL_STATE });
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private initializePhonePrefixes(): void {
    this._phonePrefixes = environment.phonePrefixes.map(p => ({
      value: p.value,
      short: p.short,
      country: p.short.split(' ')[0]
    }));

    this._dpdPhonePrefixes = this._phonePrefixes.filter(prefix =>
      DPD_COUNTRY_CODES.includes(prefix.country)
    );
  }

  private updateState(partialState: Partial<PhoneAuthState>): void {
    this.stateSubject.next({
      ...this.state,
      ...partialState
    });
  }

  private setError(code: string, translationKey: string): void {
    this.updateState({
      isLoading: false,
      error: {
        code,
        message: this.getErrorMessage(code),
        translationKey
      }
    });
  }

  private handleFirebaseError(error: any): void {
    const errorCode = error?.code || 'unknown';
    const translationKey = this.getErrorTranslationKey(errorCode);

    this.updateState({
      isLoading: false,
      error: {
        code: errorCode,
        message: error?.message || 'Unknown error',
        translationKey
      }
    });

    // Reset to phone input for certain errors
    if (['auth/session-expired', 'auth/code-expired'].includes(errorCode)) {
      this.updateState({ step: 'phone_input' });
    }
  }

  private getErrorTranslationKey(code: string): string {
    const errorMap: Record<string, string> = {
      'auth/invalid-phone-number': 'AUTH.PHONE.ERRORS.INVALID_PHONE',
      'auth/too-many-requests': 'AUTH.PHONE.ERRORS.TOO_MANY_REQUESTS',
      'auth/quota-exceeded': 'AUTH.PHONE.ERRORS.QUOTA_EXCEEDED',
      'auth/captcha-check-failed': 'AUTH.PHONE.ERRORS.CAPTCHA_FAILED',
      'auth/missing-phone-number': 'AUTH.PHONE.ERRORS.MISSING_PHONE',
      'auth/user-disabled': 'AUTH.PHONE.ERRORS.USER_DISABLED',
      'auth/invalid-app-credential': 'AUTH.PHONE.ERRORS.CONFIG_ERROR',
      'auth/operation-not-allowed': 'AUTH.PHONE.ERRORS.NOT_ALLOWED',
      'auth/billing-not-enabled': 'AUTH.PHONE.ERRORS.SERVICE_UNAVAILABLE',
      'auth/invalid-verification-code': 'AUTH.PHONE.ERRORS.INVALID_CODE',
      'auth/code-expired': 'AUTH.PHONE.ERRORS.CODE_EXPIRED',
      'auth/session-expired': 'AUTH.PHONE.ERRORS.SESSION_EXPIRED'
    };

    return errorMap[code] || 'AUTH.PHONE.ERRORS.GENERIC';
  }

  private getErrorMessage(code: string): string {
    // Fallback messages (used if translation fails)
    const messageMap: Record<string, string> = {
      'invalid-phone': 'Invalid phone number format',
      'invalid-code-format': 'Please enter the 6-digit code',
      'session-expired': 'Session expired. Please try again.',
      'too-many-attempts': 'Too many attempts. Please try again later.',
      'recaptcha-expired': 'Security check expired. Please try again.'
    };

    return messageMap[code] || 'An error occurred. Please try again.';
  }

  private startCooldownTimer(): void {
    this.cooldownTimer$.next(); // Stop any existing timer

    let remaining = RESEND_COOLDOWN_SECONDS;

    interval(1000)
      .pipe(takeUntil(this.cooldownTimer$))
      .subscribe(() => {
        remaining--;

        this.ngZone.run(() => {
          if (remaining <= 0) {
            this.updateState({
              canResend: true,
              resendCooldown: 0
            });
            this.cooldownTimer$.next();
          } else {
            this.updateState({
              resendCooldown: remaining
            });
          }
        });
      });
  }

  private extractPrefix(formattedPhone: string): string {
    // Find matching prefix
    const matchingPrefix = this._dpdPhonePrefixes.find(p =>
      formattedPhone.startsWith(p.value)
    );
    return matchingPrefix?.value || '+32';
  }

  private cleanupRecaptcha(): void {
    if (this.recaptchaVerifier) {
      try {
        this.recaptchaVerifier.clear();
      } catch (e) {
        // Ignore cleanup errors
      }
      this.recaptchaVerifier = null;
    }
  }
}
